// Collection tabs section — drives the shared slider controls in the tab
// bar (desktop) and keeps every counter in sync. Registered via the shared
// Eurus script guard so editor re-renders never double-register the Alpine
// component.
window.Eurus = window.Eurus || {};
window.Eurus.loadedScript = window.Eurus.loadedScript || new Set();

if (!window.Eurus.loadedScript.has('collection-tabs.js')) {
  window.Eurus.loadedScript.add('collection-tabs.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xCollectionTabs', () => ({
        active_tab: 0,
        sliderConfigs: new Map(),
        sectionObserver: null,
        prewarmed: false,
        ctaObserver: null,
        ctaFrame: null,
        ctaAnimations: new Map(),
        ctaEvents: null,
        motionQuery: null,
        destroyed: false,
        init() {
          this.$watch('active_tab', () => {
            this.syncActive();
          });
          this.$nextTick(() => {
            if (this.destroyed) return;
            this.ctaEvents = new AbortController();
            const signal = this.ctaEvents.signal;
            this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
            this.motionQuery.addEventListener('change', () => this.queueCtas(), { signal });
            window.addEventListener('resize', () => this.queueCtas(), { signal });
            document.addEventListener('shopify:section:unload', event => {
              if (event.target.contains(this.$root)) this.destroy();
            }, { signal });
            this.$root.querySelectorAll('.ctabs__cta').forEach(button => {
              const update = () => {
                const animation = this.ctaAnimations.get(button)?.animation;
                if (!animation) return;
                if (button.matches(':hover, :focus')) animation.pause();
                else animation.play();
              };
              ['mouseenter', 'mouseleave', 'focus', 'blur'].forEach(event => button.addEventListener(event, update, { signal }));
            });
            this.ctaObserver = new ResizeObserver(() => this.queueCtas());
            this.$root.querySelectorAll('.ctabs__ctas').forEach(row => this.ctaObserver.observe(row));
            document.fonts?.ready?.then(() => this.queueCtas());
            this.queueCtas();
            this.sectionObserver = new IntersectionObserver(entries => {
              if (!entries.some(entry => entry.isIntersecting)) return;
              this.sectionObserver.disconnect();
              this.prepareSliders();
            }, { rootMargin: '200px' });
            this.sectionObserver.observe(this.$root);
          });
        },
        destroy() {
          this.destroyed = true;
          this.sectionObserver?.disconnect();
          this.ctaObserver?.disconnect();
          this.ctaEvents?.abort();
          if (this.ctaFrame) cancelAnimationFrame(this.ctaFrame);
          this.stopCtas();
        },
        stopCtas() {
          this.ctaAnimations.forEach(({ animation, timer }, button) => {
            clearTimeout(timer);
            animation?.cancel();
            button.classList.remove('ctabs__cta--scrolling');
          });
          this.ctaAnimations.clear();
        },
        queueCtas() {
          if (this.destroyed) return;
          if (this.ctaFrame) cancelAnimationFrame(this.ctaFrame);
          this.ctaFrame = requestAnimationFrame(() => {
            this.ctaFrame = null;
            this.refreshCtas();
          });
        },
        refreshCtas() {
          this.stopCtas();
          this.$root.querySelectorAll('.ctabs__cta').forEach(button => button.classList.remove('ctabs__cta--hide-arrow'));
          if (!window.matchMedia('(max-width: 767px)').matches) return;
          const panel = this.$root.querySelectorAll('.ctabs__panel')[this.active_tab];
          if (!panel || getComputedStyle(panel).display === 'none') return;
          panel.querySelectorAll('.ctabs__cta').forEach(button => {
            const label = button.querySelector('.ctabs__cta-label');
            const text = button.querySelector('.ctabs__cta-text');
            if (!label || !text || !label.clientWidth) return;
            if (text.getBoundingClientRect().width > label.clientWidth + 1) {
              button.classList.add('ctabs__cta--hide-arrow');
            }
            const overflow = text.getBoundingClientRect().width - label.clientWidth;
            if (overflow <= 1 || this.motionQuery.matches) return;
            // Keep the ellipsis for the first 1.5s, then reveal the complete
            // accessible label without duplicating link text in the DOM.
            const entry = { animation: null, timer: null };
            this.ctaAnimations.set(button, entry);
            entry.timer = setTimeout(() => {
              if (this.destroyed) return;
              button.classList.add('ctabs__cta--scrolling');
              const distance = getComputedStyle(label).direction === 'rtl' ? overflow : -overflow;
              const travel = overflow / 30 * 1000;
              const duration = travel * 2 + 3000;
              const end = `translateX(${distance}px)`;
              const animation = text.animate([
                { transform: 'translateX(0)', offset: 0 },
                { transform: end, offset: travel / duration },
                { transform: end, offset: (travel + 1500) / duration },
                { transform: 'translateX(0)', offset: (travel * 2 + 1500) / duration },
                { transform: 'translateX(0)', offset: 1 },
              ], { duration, iterations: Infinity, easing: 'linear' });
              // Retrieve the entry from the reactive map before assigning a
              // native Animation, so pause/resume and teardown share it.
              this.ctaAnimations.get(button).animation = animation;
              if (button.matches(':hover, :focus')) animation.pause();
            }, 1500);
          });
        },
        registerSlider(el, config) {
          this.sliderConfigs.set(el, config);
          if (this.prewarmed) this.mountSlider(el);
        },
        mountSlider(el) {
          if (!el || el.splide || !window.Splide) return;
          const config = this.sliderConfigs.get(el);
          if (!config) return;
          const panel = el.closest('.ctabs__panel');
          const hidden = panel && getComputedStyle(panel).display === 'none';
          if (hidden) {
            panel.classList.add('ctabs__panel--preparing');
            panel.style.setProperty('display', 'grid', 'important');
            // Splide measures its track during mount, so hidden tabs need a
            // full-width offscreen layout until mounting is complete.
            void panel.offsetWidth;
          }
          try {
            this.$store.xSplide.load(el, config);
          } finally {
            if (hidden) {
              panel.style.setProperty('display', 'none');
              panel.classList.remove('ctabs__panel--preparing');
            }
          }
          const visibleCount = Math.max(
            1,
            Number(config.breakpoints?.[300]?.perPage) || 1,
            Number(config.breakpoints?.[1024]?.perPage) || 1,
          );
          [...el.querySelectorAll('.ctabs__slide .card-product-primary-image')]
            .slice(0, visibleCount)
            .forEach(image => {
              image.loading = 'eager';
              image.decode?.().catch(() => {});
            });
        },
        prepareSliders() {
          if (this.destroyed || this.prewarmed) return;
          if (!window.Splide) {
            window.addEventListener('load', () => this.prepareSliders(), { once: true });
            return;
          }
          this.prewarmed = true;
          this.sliderConfigs.forEach((_, el) => this.mountSlider(el));
          this.syncActive();
        },
        activeSlider() {
          const panels = this.$root.querySelectorAll('.ctabs__panel');
          return panels[this.active_tab]?.querySelector('.ctabs__slider') || null;
        },
        pad(n) {
          return n < 10 ? '0' + n : '' + n;
        },
        setCounter(counterEl, index, length) {
          if (!counterEl) return;
          const current = counterEl.querySelector('.ctabs__counter-current');
          const total = counterEl.querySelector('.ctabs__counter-total');
          if (current) current.textContent = this.pad(index + 1);
          if (total) total.textContent = this.pad(length);
        },
        syncControls(rootEl) {
          const splide = rootEl && rootEl.splide;
          if (!splide) return;
          const index = splide.index || 0;
          const length = splide.length || 0;
          this.setCounter(rootEl.querySelector('.ctabs__counter'), index, length);
          const section = rootEl.closest('.ctabs');
          if (!section) return;
          this.setCounter(section.querySelector('.ctabs__bar-controls .ctabs__counter'), index, length);
          const controller = splide.Components.Controller;
          const prevBtn = section.querySelector('.ctabs__bar-controls .ctabs__arrow--prev');
          const nextBtn = section.querySelector('.ctabs__bar-controls .ctabs__arrow--next');
          if (prevBtn) prevBtn.disabled = controller.getPrev() < 0;
          if (nextBtn) nextBtn.disabled = controller.getNext() < 0;
        },
        syncActive() {
          this.$nextTick(() => requestAnimationFrame(() => {
            if (this.destroyed) return;
            this.queueCtas();
            const slider = this.activeSlider();
            slider?.closest('.ctabs__panel')?.dispatchEvent(new CustomEvent('description-disclosure:refresh', { bubbles: true }));
            if (!slider?.splide) this.mountSlider(slider);
            if (slider?.splide) {
              slider.splide.refresh();
              this.syncControls(slider);
              return;
            }
            const bar = this.$root.querySelector('.ctabs__bar-controls .ctabs__counter');
            const panelTotal = slider?.querySelector('.ctabs__counter-total')?.textContent;
            if (bar && panelTotal) this.setCounter(bar, 0, parseInt(panelTotal, 10) || 1);
          }));
        },
        prev() {
          const slider = this.activeSlider();
          if (slider && slider.splide) slider.splide.go('<');
        },
        next() {
          const slider = this.activeSlider();
          if (slider && slider.splide) slider.splide.go('>');
        },
      }));
    });
  });
}
